import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, FileText } from '@phosphor-icons/react'
import type { Block } from '@/lib/legal/content'
import { docs } from '@/lib/legal/content'

const KIND_BY_SLUG: Record<string, keyof typeof docs> = {
  terminos: 'terms',
  privacidad: 'privacy',
}

export function LegalPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()

  const key = slug ? KIND_BY_SLUG[slug] : undefined
  if (!key) return <Navigate to="/" replace />
  const doc = docs[key]

  return (
    <div style={styles.root}>
      <div style={styles.toolbar}>
        <button
          onClick={() => navigate(-1)}
          style={styles.backBtn}
          title="Volver"
        >
          <ArrowLeft size={16} weight="regular" />
          <span>Volver</span>
        </button>
        <span style={styles.versionChip}>
          v{doc.version} · vigente desde {formatDate(doc.effectiveDate)}
        </span>
      </div>

      <article style={styles.article}>
        <header style={styles.header}>
          <div style={styles.iconWrap}>
            <FileText size={20} weight="regular" />
          </div>
          <h1 style={styles.title}>{doc.title}</h1>
          <p style={styles.intro}>{doc.intro}</p>
        </header>

        {doc.blocks.map((block, i) => (
          <BlockView key={i} block={block} />
        ))}

        <footer style={styles.footer}>
          <p style={styles.footerNote}>
            Este documento puede ser actualizado. Cuando haya cambios
            sustantivos, los Asociados recibirán aviso en la app y se les
            pedirá reaceptar.
          </p>
          <p style={styles.footerNote}>
            Fuente: <code style={styles.codeInline}>admin/src/lib/legal/content.ts</code> ·
            mantenido en sync con <code style={styles.codeInline}>app/lib/legal/content.ts</code>.
          </p>
        </footer>
      </article>
    </div>
  )
}

function BlockView({ block }: { block: Block }) {
  if (block.type === 'heading' && block.level === 1) {
    return <h2 style={styles.h1}>{block.text}</h2>
  }
  if (block.type === 'heading' && block.level === 2) {
    return <h3 style={styles.h2}>{block.text}</h3>
  }
  if (block.type === 'paragraph') {
    return <p style={styles.p}>{block.text}</p>
  }
  if (block.type === 'list') {
    return (
      <ul style={styles.list}>
        {block.items.map((item, i) => (
          <li key={i} style={styles.listItem}>
            {item}
          </li>
        ))}
      </ul>
    )
  }
  return null
}

function formatDate(iso: string): string {
  try {
    const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10))
    if (!y || !m || !d) return iso
    const meses = [
      'enero',
      'febrero',
      'marzo',
      'abril',
      'mayo',
      'junio',
      'julio',
      'agosto',
      'septiembre',
      'octubre',
      'noviembre',
      'diciembre',
    ]
    return `${d} de ${meses[m - 1]} de ${y}`
  } catch {
    return iso
  }
}

// Estilos inline. Usan las CSS vars del admin (--brand-*, --n-*) para
// integrarse con el resto del panel.
const styles: Record<string, React.CSSProperties> = {
  root: {
    width: '100%',
    maxWidth: 760,
    margin: '0 auto',
    padding: '0 8px 64px',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 18,
  },
  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    fontSize: 13,
    color: 'var(--n-700)',
    background: 'transparent',
    border: '1px solid var(--n-200)',
    borderRadius: 8,
    cursor: 'pointer',
  },
  versionChip: {
    fontSize: 11,
    color: 'var(--n-500)',
    letterSpacing: 0.3,
  },
  article: {
    background: 'var(--n-0)',
    border: '1px solid var(--n-200)',
    borderRadius: 14,
    padding: '36px 40px',
  },
  header: {
    paddingBottom: 24,
    borderBottom: '1px solid var(--n-100)',
    marginBottom: 24,
  },
  iconWrap: {
    width: 40,
    height: 40,
    borderRadius: 10,
    background: 'var(--brand-50)',
    color: 'var(--brand-600)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    letterSpacing: -0.4,
    color: 'var(--n-900)',
    margin: 0,
  },
  intro: {
    fontSize: 14,
    color: 'var(--n-600)',
    marginTop: 12,
    marginBottom: 0,
    lineHeight: 1.55,
  },
  h1: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--n-900)',
    marginTop: 28,
    marginBottom: 10,
    letterSpacing: -0.2,
  },
  h2: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--n-600)',
    marginTop: 18,
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  p: {
    fontSize: 14,
    lineHeight: 1.65,
    color: 'var(--n-800)',
    marginTop: 0,
    marginBottom: 10,
  },
  list: {
    marginTop: 4,
    marginBottom: 10,
    paddingLeft: 22,
  },
  listItem: {
    fontSize: 14,
    lineHeight: 1.65,
    color: 'var(--n-800)',
    marginBottom: 4,
  },
  footer: {
    marginTop: 36,
    paddingTop: 20,
    borderTop: '1px solid var(--n-100)',
  },
  footerNote: {
    fontSize: 12,
    color: 'var(--n-500)',
    fontStyle: 'italic',
    lineHeight: 1.55,
    marginTop: 4,
    marginBottom: 4,
  },
  codeInline: {
    fontFamily:
      'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    fontSize: 11,
    background: 'var(--n-100)',
    color: 'var(--n-700)',
    padding: '1px 5px',
    borderRadius: 4,
  },
}
